import { useState, type FC, type MouseEvent } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  Divider,
  Badge,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Notifications,
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import { OfflineStatusIndicator } from '../common/OfflineStatusIndicator';
import MobileBottomNav from './MobileBottomNav';
import {
  drawerNavItems,
  matchesNavPath,
  primaryNavItems,
  settingsNavItem,
} from './primaryNavItems';
import {
  MAIN_TOP_OFFSET,
  MAIN_TOP_OFFSET_MOBILE_SHELL,
  MOBILE_MAIN_BOTTOM_PADDING,
  MOBILE_MAIN_MIN_HEIGHT,
  SAFE_AREA_LEFT,
  SAFE_AREA_RIGHT,
  SAFE_AREA_TOP,
} from './layout.constants';
import { useMobileShell } from '../../hooks/useMobileShell';

const drawerWidth = 260;

function getPageTitle(pathname: string): string {
  const all = [...primaryNavItems, settingsNavItem];
  const exact = all.find((item) => item.path === pathname);
  if (exact) return exact.text;
  if (pathname === '/' || pathname === '') return 'Dashboard';
  const prefix = all
    .filter((item) => item.path !== '/')
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => pathname.startsWith(item.path));
  return prefix?.text ?? 'Dashboard';
}

const Layout: FC = () => {
  const theme = useTheme();
  const isShellMobile = useMobileShell();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const drawer = (
    <Box>
      <Box
        component="div"
        sx={{
          px: 2,
          py: 2,
          background: (t) => alpha(t.palette.primary.main, 0.06),
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 48 }}>
          <Box
            component="img"
            src="/brand/nexapps-profile.png"
            alt=""
            width={40}
            height={40}
            sx={{
              flexShrink: 0,
              borderRadius: 2,
              objectFit: 'cover',
              boxShadow: (t) => `0 0 0 1px ${alpha(t.palette.divider, 0.9)}`,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              component="div"
              fontWeight={700}
              color="text.primary"
              sx={{ lineHeight: 1.3, letterSpacing: '-0.01em' }}
              noWrap
            >
              Hostel Manager
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              nexapps.in
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider />
      <List>
        {drawerNavItems.map((item) => {
          const ItemIcon = item.icon;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={matchesNavPath(item.path, location.pathname)}
                onClick={() => {
                  navigate(item.path);
                }}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  minHeight: 44,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '20',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '30',
                    },
                  },
                }}
              >
                <ListItemIcon>
                  <ItemIcon />
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', minWidth: 0, width: '100%', overflowX: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: isShellMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
          ml: isShellMobile ? 0 : `${drawerWidth}px`,
          bgcolor: 'background.paper',
          color: 'text.primary',
          pt: SAFE_AREA_TOP,
          pl: SAFE_AREA_LEFT,
          pr: SAFE_AREA_RIGHT,
        }}
        elevation={0}
      >
        <Toolbar variant={isShellMobile ? 'dense' : 'regular'} sx={{ gap: 1 }}>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            {getPageTitle(location.pathname)}
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <OfflineStatusIndicator />
            <IconButton color="inherit" sx={{ minWidth: 44, minHeight: 44 }}>
              <Badge badgeContent={3} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            <IconButton onClick={handleMenuOpen} color="inherit" sx={{ minWidth: 44, minHeight: 44 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <AccountCircle />
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem>
                <Typography variant="body2" color="text.secondary">
                  Signed in as
                </Typography>
              </MenuItem>
              <MenuItem>
                <Typography variant="subtitle2">{user?.username || 'Admin'}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  navigate('/settings');
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {!isShellMobile && (
        <Box component="nav" sx={{ width: drawerWidth, flexShrink: 0 }}>
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                paddingLeft: SAFE_AREA_LEFT,
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 3 },
          pb: isShellMobile ? MOBILE_MAIN_BOTTOM_PADDING : { xs: 2, sm: 3 },
          width: isShellMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
          minWidth: 0,
          maxWidth: '100%',
          mt: isShellMobile ? MAIN_TOP_OFFSET_MOBILE_SHELL : MAIN_TOP_OFFSET,
          minHeight: { xs: MOBILE_MAIN_MIN_HEIGHT, sm: 'auto' },
          bgcolor: 'background.default',
          overflowX: 'hidden',
        }}
      >
        <Outlet />
      </Box>

      {isShellMobile && <MobileBottomNav />}
    </Box>
  );
};

export default Layout;
